import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Textarea, Text, Tooltip, Image, MessageBar, MessageBarBody } from '@fluentui/react-components';
import SyButton from '../../Common/SyButton';
import { Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserPicture } from '../../Common/UserPicture';
import { LexicalEditor, LexicalEditorRef } from '../../Common/LexicalEdit/LexicalEditor';
import './QueryMessage.scss';
import { useConversations } from '../../../store/hooks';
import { performEnterpriseGPTApiCall } from '../../../Hooks/useMiddlewareApiCall';
import { AttachmentExtensionType } from '../../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import MarkdownPresenter from '../../Common/MarkdownPresenter';

// manages the query message
export interface QueryMessageProps {
    id?: string;
    text: string;
    onUpdate: (text: string) => void;
    onEditingChange?: (isEditing: boolean) => void;
}
export interface ImageReference {
    url: string;
    fileName: string;
}
export const QueryMessage: FC<QueryMessageProps> = (props: QueryMessageProps) => {
    const { id, text, onUpdate, onEditingChange } = props;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedText, setEditedText] = useState<string>(text);
    const { t } = useTranslation();
    const { activeChat, activeGpt } = useConversations();
    const [imageUrls, setImageUrls] = useState<ImageReference[]>([]);
    const [hasAttachments, setHasAttachments] = useState<boolean>(false);

    useEffect(() => {
        setEditedText(text);
    }, [text]);

    // Notify parent when editing state changes
    useEffect(() => {
        if (onEditingChange) {
            onEditingChange(isEditing);
        }
    }, [isEditing, onEditingChange]);

    // Ref for LexicalEditor
    const editorRef = useRef<LexicalEditorRef>(null);

    // Clear editor content when exiting edit mode
    useEffect(() => {
        if (!isEditing && editorRef.current) {
            editorRef.current.clearContent();
        }
    }, [isEditing]);

    // Set editor content only when entering edit mode for the first time or when text changes
    useEffect(() => {
        if (isEditing && editorRef.current) {
            editorRef.current.setContent(text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, text]);

    // Handle Enter key to save edit
    // Use native KeyboardEvent for compatibility with LexicalEditor
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            setIsEditing(false);
            onUpdate(editedText);
            setEditedText('');
        }
    }, [editedText, onUpdate]);

    // if the user has uploaded an image, show the image preview in the query message
    useEffect(() => {
        // Find the specific prompt that matches this QueryMessage id
        const currentPrompt = activeChat?.prompts?.find(p => p.id === props.id);
        
        if (currentPrompt?.submittedAttachments && activeChat?.attachments) {
            // Check if this prompt has any attachments
            const hasAnyAttachments = currentPrompt.submittedAttachments && currentPrompt.submittedAttachments.length > 0;
            setHasAttachments(hasAnyAttachments);
            
            // Filter attachments that are both submitted with this prompt AND are images
            const promptImageAttachments = activeChat.attachments.filter((attachment) => 
                currentPrompt.submittedAttachments?.includes(attachment.name || '') && 
                attachment.extensionType === AttachmentExtensionType.Image
            );
            
            if (promptImageAttachments.length > 0) {
                setImageUrls(promptImageAttachments.map((a) => ({ url: '', fileName: a.name || '' })));
                promptImageAttachments.forEach((attachment) => {
                    performEnterpriseGPTApiCall(async (client) => {
                        if (activeChat && attachment?.name && attachment.extensionType === AttachmentExtensionType.Image) {
                            const file = await client.downloadAttachment(activeChat.id, attachment.name);
                            const url = URL.createObjectURL(file.data);
                            setImageUrls((imageUrls) => imageUrls.map((i) => (i.fileName === attachment.name ? { ...i, url } : i)));
                        }
                    });
                });
            } else {
                setImageUrls([]);
            }
        } else {
            setHasAttachments(false);
            setImageUrls([]);
        }
    }, [activeChat, props.id]);

    return (
        <div className='queryMessage'>
            <div className='inner'>
                <div className='content'>
                    {isEditing ? (
                        <>
                            {hasAttachments && (
                                <MessageBar intent='warning' className='attachment-warning'>
                                    <MessageBarBody>
                                        {t('query.attachmentWarning')}
                                    </MessageBarBody>
                                </MessageBar>
                            )}
                        
                            <LexicalEditor
                                ref={editorRef}
                                onChange={setEditedText}
                                onKeyDown={handleKeyDown}
                                placeholder={t('query.editPlaceholder') || 'Edit your message...'}
                                autoFocus={true}
                                className='lexicaleditor conversationText'
                            />


                            <div className='edit'>
                                <SyButton
                                    appearance='primary'
                                    onClick={() => {
                                        setIsEditing(false);
                                        onUpdate(editedText);
                                        setEditedText(''); // Clear edited text after update
                                    }}
                                >
                                    {t('query.saveSubmit')}
                                </SyButton>
                                <SyButton
                                    appearance='secondary'
                                    onClick={() => {
                                        setEditedText(text);
                                        setIsEditing(false);
                                    }}
                                >
                                    {t('query.cancel')}
                                </SyButton>
                            </div>
                        </>
                    ) : (
                        <>                            
                            <div className='queryMessageText'>
                                <MarkdownPresenter text={text} />
                            </div>
                            {imageUrls &&
                                imageUrls.map((imageUrl, index) => (
                                    // set the image preview to a fixed width
                                    <Image key={`img_${id}_${index}`} src={imageUrl.url} width={300} />
                                ))}
                        </>
                    )}
                </div>
                <div className='actions'>
                    <Tooltip content={isEditing ? 'Cancel' : 'Edit query'} relationship='label'>
                        <Button
                            size='medium'
                            icon={isEditing ? <X size={16} /> : <Pencil size={16} />}
                            appearance='transparent'
                            shape='circular'
                            onClick={() => {
                                setIsEditing(!isEditing);
                            }}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};
